import { updateStateData } from "../Store/actions";
import store from "../Store/store";
import {
  createCookie,
  deleteCookie,
  refreshAuthorizedUserDetails,
  sendApiRequest,
  triggerAppContextRefresh,
} from "./requestConfig";
import {
  ALL_RETAILERS_VALUE,
  BOTTOM_NAV_RETAILER_SELECTION_KEY,
  LEGACY_PAGE_LEVEL_RETAILER_SELECTION_KEYS,
  RETAILER_SELECTION_OPTIONS_KEY,
} from "./retailerSelectionConfig";
import platformConfig from "../platformConfig";
import {
  clearRetailerScopeNoticeDismissal,
  dismissOwnerOutsideRetailerNotice,
} from "../components/commonComponents/Hoc/accessControlToasts";
import { deletePaginationCookies, resetListingCaches } from "./utils";

// userManagement imports this file, so load it when the lookup runs.
const userManagement = () => require("./userManagement");

const allRetailersOption = {
  label: "All Retailers",
  value: ALL_RETAILERS_VALUE,
  id: null,
  active: true,
  is_all_retailers: true,
};

const WORKSPACE_OPTIONS_KEY = "workspace_options";
const WORKSPACE_SELECTION_OPTION_SOURCE = "workspaces";
let workspaceOptionsRequest = null;

const parseCookieValue = (cookieName, fallbackValue) => {
  const cookieRow = document.cookie.split("; ").find((row) => row.startsWith(cookieName));

  if (typeof cookieRow === "undefined") {
    return fallbackValue;
  }

  try {
    return JSON.parse(cookieRow.split("=")[1]);
  } catch (error) {
    return fallbackValue;
  }
};

const validRetailerRecord = (retailer) => {
  return (
    typeof retailer !== "undefined" &&
    retailer !== null &&
    typeof retailer.id !== "undefined" &&
    retailer.id !== null &&
    typeof retailer.name !== "undefined" &&
    retailer.name !== "" &&
    retailer.active !== false
  );
};

export const normalizeRetailerSelectionOptions = (retailers, includeAllRetailers = true) => {
  const retailerOptions = Array.isArray(retailers) ? retailers
    .filter((retailer) => validRetailerRecord(retailer))
    .map((retailer) => {
      return {
        label: retailer.name,
        value: retailer.id,
        id: retailer.id,
        active: retailer.active !== false,
        is_all_retailers: false,
      };
    })
    .sort((currentOption, nextOption) => currentOption.label.localeCompare(nextOption.label)) : [];

  if (includeAllRetailers === true && retailerOptions.length > 1) {
    return [allRetailersOption, ...retailerOptions];
  }

  return retailerOptions;
};

const validWorkspaceRecord = (workspace) => {
  return (
    typeof workspace !== "undefined" &&
    workspace !== null &&
    typeof workspace.id !== "undefined" &&
    workspace.id !== null &&
    typeof workspace.name !== "undefined" &&
    workspace.name !== ""
  );
};

export const normalizeRetailerWorkspaceSelectionOptions = (workspaces, includeAllRetailers = true) => {
  const workspaceOptions = Array.isArray(workspaces) ? workspaces
    .filter((workspace) => validWorkspaceRecord(workspace))
    .map((workspace) => {
      return {
        label: workspace.name,
        value: workspace.id,
        id: workspace.id,
        active: workspace.active !== false,
        is_all_retailers: false,
        option_source: WORKSPACE_SELECTION_OPTION_SOURCE,
      };
    }) : [];

  if (includeAllRetailers === true && workspaceOptions.length > 1) {
    return [{ ...allRetailersOption, option_source: WORKSPACE_SELECTION_OPTION_SOURCE }, ...workspaceOptions];
  }

  return workspaceOptions;
};

const filterRetailerSelectionOptions = (options, includeAllRetailers) => {
  if (includeAllRetailers === true) {
    return options;
  }

  return options.filter((option) => option.value !== ALL_RETAILERS_VALUE && option.is_all_retailers !== true);
};

const normalizeUserType = (userType) =>
  typeof userType !== "undefined" && userType !== null ? userType.toString().toLowerCase() : "";

export const userTypeCanUseRetailerSelection = (userType) =>
  platformConfig.retailerSelectionAvailableFor.includes(normalizeUserType(userType));

export const concreteRetailerSelectionOptions = (options = []) =>
  Array.isArray(options)
    ? options.filter((option) => option.value !== ALL_RETAILERS_VALUE && option.is_all_retailers !== true)
    : [];

export const hasMultipleRetailerSelectionOptions = (options = []) =>
  concreteRetailerSelectionOptions(options).length > 1;

/** Redux first, then the cookie snapshot used before options hydrate. */
export const resolveRetailerSelectionOptions = (stateData) => {
  const fromState =
    typeof stateData !== "undefined" && typeof stateData[RETAILER_SELECTION_OPTIONS_KEY] !== "undefined"
      ? stateData[RETAILER_SELECTION_OPTIONS_KEY]
      : [];
  if (Array.isArray(fromState) && fromState.length !== 0) {
    return fromState;
  }
  return checkRetailerSelectionOptionsFromCookies().options;
};

/**
 * All-Retailers-only actions (user management, campaign Sections, Add User company).
 *
 * Lock only when the bottom-nav switcher actually exists (2+ companies) and the
 * user picked a specific retailer. A single-company tenant still stores that
 * company with is_all_retailers: false — that must stay unlocked.
 */
export const isLockedOutsideAllRetailers = (selectedOption = {}, options = []) => {
  if (hasMultipleRetailerSelectionOptions(options) !== true) {
    return false;
  }
  if (typeof selectedOption.value === "undefined") {
    return false;
  }
  return selectedOption.is_all_retailers !== true && selectedOption.value !== ALL_RETAILERS_VALUE;
};

export const checkRetailerSelectionOptionsFromCookies = () => {
  const options = parseCookieValue(RETAILER_SELECTION_OPTIONS_KEY, []);

  return {
    success: Array.isArray(options) && options.length !== 0,
    options: Array.isArray(options) ? options : [],
  };
};

export const checkRetailerSelectionFromCookies = (selectionKey) => {
  const selectedOption = parseCookieValue(selectionKey, {});

  return {
    success: typeof selectedOption.value !== "undefined",
    selected_option: selectedOption,
  };
};

export const getBottomNavRetailerSelectionFromCookies = () => {
  return checkRetailerSelectionFromCookies(BOTTOM_NAV_RETAILER_SELECTION_KEY).selected_option;
};

const selectedOptionAvailable = (options, selectedOption) => {
  return options.find((option) => option.value === selectedOption.value);
};

const defaultRetailerSelection = (options) => {
  if (options.length === 0) {
    return {};
  }

  const allRetailers = options.find((option) => option.value === ALL_RETAILERS_VALUE);
  return typeof allRetailers !== "undefined" ? allRetailers : options[0];
};

const deleteLegacyPageLevelRetailerCookies = () => {
  LEGACY_PAGE_LEVEL_RETAILER_SELECTION_KEYS.forEach((selectionKey) => {
    deleteCookie(selectionKey);
  });
};

export const saveRetailerSelection = (selectionKey, selectedOption) => {
  store.dispatch(
    updateStateData({
      [selectionKey]: selectedOption,
    })
  );
  createCookie(selectionKey, JSON.stringify(selectedOption));

  return selectedOption;
};

const retailerSelectionFromStateOrCookies = (selectionKey) => {
  const currentStateData = store.getState().userSelections;
  const selectedFromState =
    typeof currentStateData[selectionKey] !== "undefined" ? currentStateData[selectionKey] : {};
  const selectedFromCookies = checkRetailerSelectionFromCookies(selectionKey);

  return typeof selectedFromState.value !== "undefined" ? selectedFromState : selectedFromCookies.selected_option;
};

export const getRetailerSelection = (selectionKey, config = {}) => {
  const fallbackToGlobal =
    typeof config.fallbackToGlobal !== "undefined" ? config.fallbackToGlobal : true;
  const selectedOption = retailerSelectionFromStateOrCookies(selectionKey);

  if (typeof selectedOption.value !== "undefined") {
    return selectedOption;
  }

  return fallbackToGlobal === true
    ? retailerSelectionFromStateOrCookies(BOTTOM_NAV_RETAILER_SELECTION_KEY)
    : {};
};

const isAllRetailersSelection = (selectedOption) => {
  return selectedOption.value === ALL_RETAILERS_VALUE || selectedOption.is_all_retailers === true;
};

const retailerOptionId = (option) => {
  if (typeof option?.id !== "undefined" && option.id !== null && option.id !== ALL_RETAILERS_VALUE) {
    return option.id;
  }
  if (typeof option?.value !== "undefined" && option.value !== null && option.value !== ALL_RETAILERS_VALUE) {
    return option.value;
  }
  return "";
};

// Company id for filter_view_company_id. Cyreen, tenant, and agency only.
// One retailer: that id. Several: the bottom-nav choice. All Retailers sends nothing.
export const resolveFilterViewCompanyId = () => {
  // false: a missing cookie during login must not log the user out from inside a GET.
  const loggedUserDetails = userManagement().checkUserDetailsFromCookie(false);
  const userType = normalizeUserType(
    loggedUserDetails.success === true ? loggedUserDetails.user_detail.user_type : ""
  );
  if (userTypeCanUseRetailerSelection(userType) !== true) {
    return "";
  }

  const concreteOptions = concreteRetailerSelectionOptions(
    resolveRetailerSelectionOptions(store.getState().userSelections)
  );
  if (concreteOptions.length === 0) {
    return "";
  }
  if (concreteOptions.length === 1) {
    return retailerOptionId(concreteOptions[0]);
  }

  const selectedOption = getRetailerSelection(BOTTOM_NAV_RETAILER_SELECTION_KEY);
  if (typeof selectedOption?.value === "undefined" || isAllRetailersSelection(selectedOption) === true) {
    return "";
  }
  return retailerOptionId(selectedOption);
};

const sameCompanyId = (left, right) => String(left) === String(right);

const collectCompanyIds = (node, ids) => {
  if (node == null || node.id == null) {
    return;
  }
  ids.add(String(node.id));
  const children = Array.isArray(node.children) ? node.children : [];
  children.forEach((child) => collectCompanyIds(child, ids));
};

// Hierarchy is stored as a root wrapper, or as retailer groups when there is no root.
const retailerGroupsFromHierarchy = (hierarchy) => {
  const tree = Array.isArray(hierarchy?.retailers) ? hierarchy.retailers : [];
  if (tree.length === 1 && tree[0]?.type === "root") {
    return Array.isArray(tree[0].children) ? tree[0].children : [];
  }
  return tree.filter((node) => node?.type !== "root");
};

const pathIncludesCompany = (treePath, companyId) => {
  const target = String(companyId);
  if (Array.isArray(treePath)) {
    return treePath.some((entry) => {
      if (entry != null && typeof entry === "object") {
        return sameCompanyId(entry.id, target);
      }
      return sameCompanyId(entry, target);
    });
  }
  if (typeof treePath === "string" && treePath !== "") {
    return treePath.split(/[/|,>\s]+/).some((part) => part === target);
  }
  return false;
};

const companyRecord = (map, companyId) => {
  if (map == null || companyId == null || companyId === "") {
    return undefined;
  }
  return map[companyId] ?? map[String(companyId)];
};

const companyInList = (list, companyId) => {
  if (Array.isArray(list) !== true) {
    return undefined;
  }
  return list.find((company) => sameCompanyId(company?.id, companyId));
};

const selectedRetailerLabel = () => {
  const selectedOption = getRetailerSelection(BOTTOM_NAV_RETAILER_SELECTION_KEY);
  if (
    isAllRetailersSelection(selectedOption) !== true &&
    typeof selectedOption?.label === "string" &&
    selectedOption.label !== ""
  ) {
    return selectedOption.label;
  }

  const concreteOptions = concreteRetailerSelectionOptions(
    resolveRetailerSelectionOptions(store.getState().userSelections)
  );
  if (concreteOptions.length === 1 && typeof concreteOptions[0].label === "string" && concreteOptions[0].label !== "") {
    return concreteOptions[0].label;
  }
  return "this retailer";
};

/** Bottom-nav retailer name when filter_view is scoped; empty string for All Retailers. */
export const scopedRetailerDisplayName = () => {
  if (resolveFilterViewCompanyId() === "") {
    return "";
  }
  return selectedRetailerLabel();
};

// Null when the notice is not needed: one retailer, All Retailers, no owner, or the owner sits under the selected retailer.
// { retailerName } when a specific retailer is selected and the owner is outside it.
export const ownerOutsideSelectedRetailer = (ownerCompanyId) => {
  if (ownerCompanyId == null || ownerCompanyId === "") {
    return null;
  }

  const stateData = store.getState().userSelections;
  // One assigned retailer has no filter dropdown, so there is nothing to switch to All Retailers.
  if (hasMultipleRetailerSelectionOptions(resolveRetailerSelectionOptions(stateData)) !== true) {
    return null;
  }

  const selectedRetailerId = resolveFilterViewCompanyId();
  if (selectedRetailerId === "") {
    return null;
  }

  const selectedGroup = retailerGroupsFromHierarchy(stateData.company_hierarchy).find((group) =>
    sameCompanyId(group.id, selectedRetailerId)
  );

  if (selectedGroup != null) {
    const companyIds = new Set();
    collectCompanyIds(selectedGroup, companyIds);
    if (companyIds.has(String(ownerCompanyId))) {
      return null;
    }
    return { retailerName: selectedRetailerLabel() };
  }

  // Hierarchy is not in memory. The owner's tree_path is the fallback when a loaded list has it.
  const ownerRecord =
    companyRecord(stateData.company_hierarchy_by_id, ownerCompanyId) ??
    companyRecord(stateData.company_id_option_obj, ownerCompanyId) ??
    companyInList(stateData.store_level_retailers_list, ownerCompanyId) ??
    companyInList(stateData.retailers_list, ownerCompanyId);
  const treePath = ownerRecord?.tree_path;
  if (treePath == null || treePath === "" || (Array.isArray(treePath) && treePath.length === 0)) {
    return null;
  }
  if (sameCompanyId(ownerCompanyId, selectedRetailerId) || pathIncludesCompany(treePath, selectedRetailerId)) {
    return null;
  }
  return { retailerName: selectedRetailerLabel() };
};

/**
 * True when the global (bottom nav) filter is locked to one specific retailer.
 *
 * Used to hide All-Retailers-only actions (user management, campaign Sections).
 *
 * stateData is optional and only there so components can pass their Redux slice
 * and re-render whenever the selection changes.
 */
export const bottomNavScopedToSpecificRetailer = (stateData) => {
  const currentStateData =
    typeof stateData !== "undefined" ? stateData : store.getState().userSelections;
  const selectedFromState = currentStateData[BOTTOM_NAV_RETAILER_SELECTION_KEY];
  const selectedOption =
    typeof selectedFromState !== "undefined" && typeof selectedFromState.value !== "undefined"
      ? selectedFromState
      : getBottomNavRetailerSelectionFromCookies();

  return isLockedOutsideAllRetailers(selectedOption, resolveRetailerSelectionOptions(currentStateData));
};

/**
 * Global notice for tenant / Cyreen / agency users when bottom nav is scoped
 * to one retailer (same condition as user-management and Sections locks).
 */
export const shouldShowAllRetailersOnlyWorkspaceNotice = (userType, stateData) => {
  if (userTypeCanUseRetailerSelection(userType) !== true) {
    return false;
  }

  return bottomNavScopedToSpecificRetailer(stateData) === true;
};

/**
 * number_root sent to the embeddable dashboard.
 *
 * Uses the user's current workspace root (All Retailers, or the retailer
 * already chosen in the bottom nav).
 */
export const resolveEmbeddableNumberRoot = (fallbackRootCompanyId) => {
  if (typeof fallbackRootCompanyId === "undefined" || fallbackRootCompanyId === null) {
    return "";
  }

  return fallbackRootCompanyId.toString();
};

export const applyRetailerSelection = async (selectionKey, selectedOption) => {
  if (typeof selectedOption === "undefined" || typeof selectedOption.value === "undefined") {
    return {
      success: false,
    };
  }

  const previousSelection = getRetailerSelection(selectionKey, { fallbackToGlobal: false });
  const workspaceSwitchTarget = isAllRetailersSelection(selectedOption) === true ? "master" : selectedOption.value;
  const switchWorkspaceInfo = await sendApiRequest("workspaces/switch/" + workspaceSwitchTarget, {}, "POST");

  if (
    typeof switchWorkspaceInfo.success === "undefined" ||
    switchWorkspaceInfo.success !== true
  ) {
    return {
      success: false,
    };
  }

  store.dispatch(
    updateStateData({
      show_workspace_list: false,
    })
  );

  const userContextInfo = await refreshAuthorizedUserDetails();

  if (typeof userContextInfo.success === "undefined" || userContextInfo.success !== true) {
    return {
      success: false,
    };
  }

  const switchedFromAllRetailersToSpecific =
    selectionKey === BOTTOM_NAV_RETAILER_SELECTION_KEY &&
    isAllRetailersSelection(previousSelection) === true &&
    isAllRetailersSelection(selectedOption) !== true;

  if (switchedFromAllRetailersToSpecific === true) {
    clearRetailerScopeNoticeDismissal();
  }

  // The hidden-entry notice only applies while one retailer is selected.
  if (
    selectionKey === BOTTOM_NAV_RETAILER_SELECTION_KEY &&
    isAllRetailersSelection(selectedOption) === true
  ) {
    dismissOwnerOutsideRetailerNotice();
  }

  const savedRetailerSelection = saveRetailerSelection(selectionKey, selectedOption);
  deleteLegacyPageLevelRetailerCookies();

  if (selectionKey === BOTTOM_NAV_RETAILER_SELECTION_KEY) {
    deletePaginationCookies();
    resetListingCaches();
  }

  triggerAppContextRefresh("retailer_selection", {
    selection_key: selectionKey,
    selected_option: savedRetailerSelection,
  });

  return {
    success: true,
    selected_option: savedRetailerSelection,
  };
};

const saveRetailerSelectionOptions = (options) => {
  store.dispatch(
    updateStateData({
      [RETAILER_SELECTION_OPTIONS_KEY]: options,
    })
  );
  createCookie(RETAILER_SELECTION_OPTIONS_KEY, JSON.stringify(options));
};

const hydrateRetailerSelection = (selectionKey, options) => {
  const currentStateData = store.getState().userSelections;
  const selectedFromState = typeof currentStateData[selectionKey] !== "undefined" ? currentStateData[selectionKey] : {};
  const selectedFromCookies = checkRetailerSelectionFromCookies(selectionKey);
  const requestedSelection = typeof selectedFromState.value !== "undefined" ? selectedFromState : selectedFromCookies.selected_option;
  const selectedOption = selectedOptionAvailable(options, requestedSelection) || defaultRetailerSelection(options);

  if (typeof selectedOption.value !== "undefined") {
    saveRetailerSelection(selectionKey, selectedOption);
  }

  return selectedOption;
};

const workspaceOptionsFromStateOrCookies = () => {
  const currentStateData = store.getState().userSelections;
  const optionsFromState = Array.isArray(currentStateData[WORKSPACE_OPTIONS_KEY])
    ? currentStateData[WORKSPACE_OPTIONS_KEY]
    : [];
  const optionsFromCookies = parseCookieValue(WORKSPACE_OPTIONS_KEY, []);

  if (optionsFromState.length !== 0) {
    return optionsFromState;
  }

  return Array.isArray(optionsFromCookies) ? optionsFromCookies : [];
};

const fetchWorkspaceOptions = async () => {
  if (workspaceOptionsRequest === null) {
    // Retailer dropdown options. Do not scope this list by the retailer already chosen.
    workspaceOptionsRequest = sendApiRequest("workspaces", {}, "GET", { skipRetailerViewFilter: true }).finally(() => {
      workspaceOptionsRequest = null;
    });
  }

  return workspaceOptionsRequest;
};

const retailerWorkspaceOptionsFromCache = (includeAllRetailers) => {
  const currentStateData = store.getState().userSelections;
  const optionsFromState = Array.isArray(currentStateData[RETAILER_SELECTION_OPTIONS_KEY])
    ? currentStateData[RETAILER_SELECTION_OPTIONS_KEY]
    : [];
  const optionsFromCookies = checkRetailerSelectionOptionsFromCookies();
  const options = optionsFromState.length !== 0 ? optionsFromState : optionsFromCookies.options;
  const workspaceOptions = Array.isArray(options)
    ? options.filter((option) => option.is_all_retailers === true || option.option_source === WORKSPACE_SELECTION_OPTION_SOURCE)
    : [];

  if (workspaceOptions.length !== options.length || workspaceOptions.length === 0) {
    return [];
  }

  return filterRetailerSelectionOptions(workspaceOptions, includeAllRetailers);
};

export const loadRetailerWorkspaceSelectionOptions = async (selectionKey, config = {}) => {
  const includeAllRetailers = typeof config.includeAllRetailers !== "undefined" ? config.includeAllRetailers : true;
  const persistSelection = typeof config.persistSelection !== "undefined" ? config.persistSelection : true;
  const cachedOptions = retailerWorkspaceOptionsFromCache(includeAllRetailers);

  if (cachedOptions.length !== 0) {
    return {
      success: true,
      options: cachedOptions,
      selected_option: persistSelection === true ? hydrateRetailerSelection(selectionKey, cachedOptions) : {},
    };
  }

  let workspaces = workspaceOptionsFromStateOrCookies();

  if (workspaces.length === 0) {
    const workspaceResponse = await fetchWorkspaceOptions();

    if (typeof workspaceResponse.success === "undefined" || workspaceResponse.success !== true) {
      return {
        success: false,
        options: [],
        selected_option: {},
      };
    }

    workspaces = Array.isArray(workspaceResponse.response) ? workspaceResponse.response : [];
  }

  const options = normalizeRetailerWorkspaceSelectionOptions(workspaces, includeAllRetailers);

  if (options.length === 0) {
    return {
      success: false,
      options: [],
      selected_option: {},
    };
  }

  if (persistSelection === true) {
    saveRetailerSelectionOptions(options);
  }

  return {
    success: true,
    options,
    selected_option: persistSelection === true ? hydrateRetailerSelection(selectionKey, options) : {},
  };
};

export const loadRetailerAccessAvailability = async (userType, config = {}) => {
  const selectionKey =
    typeof config.selectionKey !== "undefined" ? config.selectionKey : BOTTOM_NAV_RETAILER_SELECTION_KEY;

  if (userTypeCanUseRetailerSelection(userType) === false) {
    return {
      success: true,
      available: false,
      options: [],
    };
  }

  const retailerSelectionInfo = await loadRetailerWorkspaceSelectionOptions(selectionKey, {
    includeAllRetailers: false,
    persistSelection: false,
  });

  if (typeof retailerSelectionInfo.success === "undefined" || retailerSelectionInfo.success !== true) {
    return {
      success: false,
      available: false,
      options: [],
    };
  }

  const options = concreteRetailerSelectionOptions(retailerSelectionInfo.options);

  return {
    success: true,
    available: hasMultipleRetailerSelectionOptions(options),
    options,
  };
};

export const loadRetailerSelectionOptions = async (selectionKey, config = {}) => {
  const includeAllRetailers = typeof config.includeAllRetailers !== "undefined" ? config.includeAllRetailers : true;
  const persistSelection = typeof config.persistSelection !== "undefined" ? config.persistSelection : true;
  const currentStateData = store.getState().userSelections;
  const optionsFromState = typeof currentStateData[RETAILER_SELECTION_OPTIONS_KEY] !== "undefined"
    ? currentStateData[RETAILER_SELECTION_OPTIONS_KEY]
    : [];
  const optionsFromCookies = checkRetailerSelectionOptionsFromCookies();

  if (persistSelection === true && (optionsFromState.length !== 0 || optionsFromCookies.success === true)) {
    const options = filterRetailerSelectionOptions(
      optionsFromState.length !== 0 ? optionsFromState : optionsFromCookies.options,
      includeAllRetailers
    );

    if (persistSelection === true) {
      saveRetailerSelectionOptions(options);
    }

    return {
      success: true,
      options,
      selected_option: persistSelection === true ? hydrateRetailerSelection(selectionKey, options) : {},
    };
  }

  const retailerRootsResponse = await sendApiRequest("companies/retailer-roots", {}, "GET", { skipRetailerViewFilter: true });

  if (typeof retailerRootsResponse.success !== "undefined" && retailerRootsResponse.success === true) {
    const options = normalizeRetailerSelectionOptions(retailerRootsResponse.response, includeAllRetailers);

    if (options.length === 0) {
      return {
        success: false,
        options: [],
        selected_option: {},
      };
    }

    if (persistSelection === true) {
      saveRetailerSelectionOptions(options);
    }

    return {
      success: true,
      options,
      selected_option: persistSelection === true ? hydrateRetailerSelection(selectionKey, options) : {},
    };
  }

  return {
    success: false,
    options: [],
    selected_option: {},
  };
};

// Same list as the old Tenant Company dropdown: retailer-roots, no All Retailers row.
export const loadRetailerCompanyIdsForAccountSave = async () => {
  const companiesInfo = await loadRetailerSelectionOptions("tenant_user_company_options", {
    includeAllRetailers: false,
    persistSelection: false,
  });
  if (companiesInfo.success !== true || !Array.isArray(companiesInfo.options)) {
    return [];
  }

  return companiesInfo.options
    .map((option) => option.id)
    .filter((id) => typeof id !== "undefined" && id !== null && id !== "");
};
